import {PictureSetting} from "../../common/constants/pictureSettings";
import {CmPictureType} from "../types";
import {usePreviewContext} from "../../context/PreviewContext";
import {getAltText, getPictureUri} from "./CCOPicture";
import Image from "next/image";
import React from "react";
import {isOldIpad} from "../../utility/userAgent";
import {isCdnUrl} from "../../utility/cdnPictureUtil";

export interface Props {
    pictureSetting: PictureSetting,
    picture: CmPictureType | null | undefined | any,
    className?: string,
    alt?: string,
    pictureStyle?: any
    imageStyle?: any
    hasPriority?: boolean
    dataCypress?: string
}

const CCOResponsivePicture = (props: Props) => {
    const preview = usePreviewContext().isPreview;
    const sources = createSources(props.picture, props.pictureSetting, preview);
    return (
        <picture className={props.className || ""} style={props.pictureStyle}>
            {!isOldIpad() && sources.map((source, index) => {
                return source && <source key={index} media={source.media} srcSet={source.srcset}/>;
            })}
            {renderImage(props, preview)}
        </picture>
    )
}

function createSources(picture: CmPictureType | any, pictureSetting: PictureSetting, isPreview: boolean) {
    return pictureSetting.sources
        // The list of sources needs to be sorted downwards.
        // The browser will go top down.
        // The first source tag that returns true (media query) will be displayed.
        .sort((a, b) => b.minWidthBreakpoint! - a.minWidthBreakpoint!)
        .map(source => {
            return {
                media: "(min-width: " + source.minWidthBreakpoint + "px)",
                // getPictureUri's signature is (width, isPreview, picture, orientation) - resolves via the
                // canonical CDN/CoreMedia decision tree (celum.xml forceCelum/cdnConfig.enabled).
                srcset: getPictureUri(source.width.toString(), isPreview, picture, source.cropName)
            }
        })
}

function renderImage(props: Props, isPreview: boolean): JSX.Element {
    const defaultPicture = props.pictureSetting.defaultPicture;
    const defaultImageUrl = getPictureUri(defaultPicture.width.toString(), isPreview, props.picture, defaultPicture.cropName);
    const alt = props.alt ?? getAltText(props.picture.alt, props.picture.title);
    const title = props.picture.title;
    const className = 'rae-responsive-picture ' + (props.className || '');

    return props.hasPriority ?
        <Image data-cypress={props.dataCypress} className={className}
               alt={alt}
               style={props.imageStyle}
               src={defaultImageUrl || ''}
               title={title || ''}
               fill={true}
               priority={true}
               // Der Preload-Hinweis bleibt, die Bildverarbeitung entfällt: siehe isCdnUrl.
               unoptimized={isCdnUrl(defaultImageUrl)}
        /> :
        /*
         * next/image is used above where it earns its keep - the preload/priority hint for the
         * LCP image. For the lazy path a plain <img> is deliberate: getPictureUri already returns
         * a CMS URL with the crop name and target width filled in and the extension rewritten to
         * WebP, so routing it through /_next/image would be a second resize and re-encode of an
         * already prepared asset. On top of that, next/image inside a <picture> with art-directed
         * <source> siblings is contradictory - the sources win and its own srcSet never applies.
         */
        // eslint-disable-next-line @next/next/no-img-element -- see the note above
        <img data-cypress={props.dataCypress} className={(className)} alt={alt}
             style={props.imageStyle}
             src={defaultImageUrl || ''} title={title || ''} loading='lazy'/>
}


export default CCOResponsivePicture;